import axios from 'axios'
import { apiClient } from '../api/client'

/**
 * Bir {@link Blob}'u tarayıcıda dosya olarak indirir (geçici object URL + gizli anchor). Sunucudan gelen
 * ikili yanıtları (ör. Excel dışa aktarma) kaydetmek için kullanılır (035).
 */
export function saveBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  a.remove()
  URL.revokeObjectURL(url)
}

/** Excel dışa aktarma sorgu parametreleri; boş değerler sunucuya HİÇ gönderilmez. */
export type ExportParams = Record<string, string | number | boolean | null | undefined>

/**
 * **Generic Excel (.xlsx) indirme yardımcısı.** Her ekran, kendi liste filtrelerini verip bunu çağırır;
 * istek/dosya adı/indirme mekaniği tek noktadan yönetilir:
 *
 * ```ts
 * await downloadXlsx('/management/activities/export', aktifFiltreler, 'etkinlik-listesi.xlsx')
 * ```
 *
 * - Boş filtreler (`''`/`null`/`undefined`) `undefined` olarak geçilir → axios bunları sorguya eklemez
 *   (sunucu "boş string" filtresi görmez).
 * - Dosya adı yanıttaki `Content-Disposition`'dan okunur (RFC 5987 `filename*` varsa o tercih edilir);
 *   başlık yoksa/okunamıyorsa çağıranın verdiği ada düşülür.
 * - Hata durumunda blob gövdesi metne çevrilip JSON'a ayrıştırılır → çağıran, backend'in **lokalize**
 *   mesajını (`getApiErrorMessage`) okuyabilir; aksi halde genel hata gösterir.
 */
export async function downloadXlsx(
  url: string,
  params: ExportParams,
  fallbackFilename: string,
): Promise<void> {
  try {
    const response = await apiClient.get<Blob>(url, {
      params: pruneEmpty(params),
      responseType: 'blob',
    })
    const disposition = readHeader(response.headers, 'content-disposition')
    saveBlob(response.data, filenameFromDisposition(disposition, fallbackFilename))
  } catch (err) {
    throw await withReadableErrorBody(err)
  }
}

/** Boş filtreleri ayıklar: `''`, `null`, `undefined` → parametre hiç gönderilmez. */
export function pruneEmpty(params: ExportParams): Record<string, string | number | boolean> {
  const result: Record<string, string | number | boolean> = {}
  for (const [key, value] of Object.entries(params)) {
    if (value === null || value === undefined) continue
    if (typeof value === 'string') {
      const trimmed = value.trim()
      if (trimmed === '') continue
      result[key] = trimmed
      continue
    }
    result[key] = value
  }
  return result
}

/**
 * `Content-Disposition`'dan dosya adını çıkarır. Türkçe karakterler için RFC 5987 (`filename*=UTF-8''…`)
 * tercih edilir; yoksa düz `filename="…"` kullanılır; ikisi de yoksa `fallback` döner.
 */
export function filenameFromDisposition(
  disposition: string | undefined,
  fallback: string,
): string {
  if (!disposition) return fallback

  const encoded = /filename\*\s*=\s*([^']*)'([^']*)'([^;]+)/i.exec(disposition)
  if (encoded) {
    try {
      return decodeURIComponent(encoded[3].trim())
    } catch {
      // Bozuk yüzde-kodlama → düz filename'e düş.
    }
  }

  const plain = /filename\s*=\s*"([^"]*)"/i.exec(disposition) ?? /filename\s*=\s*([^;]+)/i.exec(disposition)
  const name = (plain?.[1] ?? '').trim()
  return name === '' ? fallback : name
}

/** Axios yanıt başlıklarını (düz nesne ya da AxiosHeaders) tek biçimde okur. */
function readHeader(headers: unknown, name: string): string | undefined {
  if (!headers || typeof headers !== 'object') return undefined
  const bag = headers as Record<string, unknown> & { get?: (key: string) => unknown }
  const value = typeof bag.get === 'function' ? bag.get(name) : bag[name]
  return typeof value === 'string' ? value : undefined
}

/**
 * `responseType: 'blob'` istekte hata gövdesi de Blob olarak gelir → JSON hata sözleşmesi (`{code,message}`)
 * okunamaz. Gövdeyi metne çevirip ayrıştırır ve hatanın üzerine yazar; böylece merkezi hata yardımcıları
 * (`getApiErrorCode` / `getApiErrorMessage`) normal isteklerde olduğu gibi çalışır.
 */
async function withReadableErrorBody(err: unknown): Promise<unknown> {
  if (!axios.isAxiosError(err) || !err.response) return err
  const data: unknown = err.response.data
  if (typeof Blob === 'undefined' || !(data instanceof Blob)) return err
  try {
    const text = await data.text()
    err.response.data = text === '' ? undefined : JSON.parse(text)
  } catch {
    // Gövde JSON değil (ör. proxy HTML hata sayfası) → olduğu gibi bırakılır, genel hata gösterilir.
  }
  return err
}
